import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  HandCoins,
  Search,
  Mail,
  Phone,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Settings,
  Info,
  FileSpreadsheet,
  Loader2,
  UserCog,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import type { PromiseToPay } from '@/types/collections';
import { usePromisesPageData, useBulkReassignPromises, useBulkUpdatePromiseStatus, type GetPromisesToPayParams } from '@/hooks/usePromisesToPayData';
import { useUsersList } from '@/hooks/useUsersData';
import { Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { exportPromisesToExcel } from '@/lib/promise-export-utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';
import { getPageNumbers } from '@/lib/pagination-utils';
import { HelpSupportButton } from '@/components/help-support';
import { LogCallModal, ReassignUserModal, UpdateStatusModal, SendEmailModal } from '@/components/collections/action-modals';

const DEFAULT_PROMISE_COLUMNS: ColumnDefinition[] = [
  { id: 'select', label: 'Select', visible: true, order: 0 },
  { id: 'promiseId', label: 'Promise ID', visible: true, order: 1 },
  { id: 'customerName', label: 'Customer', visible: true, order: 2 },
  { id: 'caseNumber', label: 'Case Number', visible: true, order: 3 },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 4 },
  { id: 'promiseDate', label: 'Promise Date', visible: true, order: 5 },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 6 },
  { id: 'amount', label: 'Amount', visible: true, order: 7 },
  { id: 'transactions', label: 'Transactions', visible: true, order: 8 },
  { id: 'status', label: 'Status', visible: true, order: 9 },
  { id: 'method', label: 'Method', visible: true, order: 10 },
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 11 },
  { id: 'customerEmail', label: 'Email', visible: false, order: 12 },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 13 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 14 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15 },
  { id: 'notes', label: 'Notes', visible: false, order: 16 },
];

// Map frontend status display names to backend status codes
// StatusCode values from v_PromiseToPay_List view: Active, Broken, Paid, Partially Paid, Closed
const statusToBackendCode: Record<string, string> = {
  Active: 'Active',
  Broken: 'Broken',
  Paid: 'Paid',
  'Partially Paid': 'Partially Paid',
  Closed: 'Closed',
};

// Map frontend sort field names to backend sort field names
const sortFieldToBackend: Record<string, string> = {
  promiseId: 'PromiseId',
  customerName: 'CustomerName',
  caseNumber: 'CaseNumber',
  promiseDate: 'PromiseDate',
  daysOverdue: 'DaysOverdue',
  amount: 'PromisedAmount',
  transactions: 'InvoiceCount',
  status: 'Status',
  method: 'ConfirmationMethod',
  customerAccountNumber: 'CustomerAccountNumber',
  customerEmail: 'ContactEmail',
  customerPhone: 'ContactPhone',
  createdDate: 'CreatedAt',
  createdBy: 'CreatedByName',
  notes: 'Notes',
};

const PromisesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assignedUserFilter, setAssignedUserFilter] = useState<string>('all');
  const [metricFilter, setMetricFilter] = useState<'all' | 'active' | 'broken' | 'dueThisWeek'>('all');
  const [selectedPromiseForModal, setSelectedPromiseForModal] = useState<PromiseToPay | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [reassignUserModalOpen, setReassignUserModalOpen] = useState(false);
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [selectedPromiseIds, setSelectedPromiseIds] = useState<Set<string>>(new Set());
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting state - Default sort: Promise Date (asc)
  type SortField = "promiseId" | "customerName" | "caseNumber" | "promiseDate" | "daysOverdue" | "amount" | "transactions" | "status" | "method" | "customerAccountNumber" | "customerEmail" | "customerPhone" | "createdDate" | "createdBy" | "notes";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>("promiseDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Column preferences
  const { columns, visibleColumns, savePreferences, resetToDefaults } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.PromiseToPay, DEFAULT_PROMISE_COLUMNS);

  // Build server-side query params from current filter/sort/page state
  const queryParams = useMemo((): GetPromisesToPayParams => {
    const params: GetPromisesToPayParams = {
      page: currentPage,
      pageSize: itemsPerPage,
    };

    // Search
    if (searchQuery) {
      params.search = searchQuery;
    }

    // Status filter - derive from metricFilter + statusFilter
    // KPI cards are quick filters that are additive with other filters
    if (metricFilter === 'active') {
      params.status = 'Active';
    } else if (metricFilter === 'broken') {
      params.status = 'Broken';
    } else if (metricFilter === 'dueThisWeek') {
      const today = new Date();
      const dueEnd = new Date();
      dueEnd.setDate(dueEnd.getDate() + 7);
      params.dateFrom = today.toISOString().split('T')[0];
      params.dateTo = dueEnd.toISOString().split('T')[0];
      // Due This Week is a date-range-only filter; apply status dropdown if user has one selected
      if (statusFilter !== 'all') {
        params.status = statusToBackendCode[statusFilter] ?? statusFilter;
      }
    } else if (statusFilter !== 'all') {
      params.status = statusToBackendCode[statusFilter] ?? statusFilter;
    }

    // User filter
    if (assignedUserFilter !== 'all') {
      params.assignedUserId = Number(assignedUserFilter);
    }

    // Sorting
    if (sortField && sortDirection) {
      params.sortBy = sortFieldToBackend[sortField] ?? 'PromiseDate';
      params.sortOrder = sortDirection === 'desc' ? 'desc' : 'asc';
    }

    return params;
  }, [currentPage, itemsPerPage, searchQuery, statusFilter, assignedUserFilter, metricFilter, sortField, sortDirection]);

  // Fetch data from server with all params
  const { promises, pagination, kpis, isLoading } = usePromisesPageData(queryParams);

  // Derive pagination values from server response
  const totalPages = pagination.totalPages;
  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = Math.min(startIndex + pagination.pageSize, pagination.totalItems);

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  // User filter options from the users list (not from current page data)
  const { data: usersList, isLoading: isLoadingUsers } = useUsersList();

  const userFilterOptions = useMemo(() => {
    if (!usersList) return [];
    return usersList.map(x => ({
      id: String(x.id),
      name: `${x.firstName} ${x.lastName}`.trim(),
      avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(`${x.firstName} ${x.lastName}`.trim())}&background=random&size=128`,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [usersList]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField("promiseDate");
        setSortDirection("asc");
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const handleViewDetails = (promise: PromiseToPay) => {
    navigate(`/collections/promises/${promise.id}`);
  };

  const handleEmailCustomer = (promise: PromiseToPay) => {
    setSelectedPromiseForModal(promise);
    setEmailModalOpen(true);
  };

  const handleLogCall = (promise: PromiseToPay) => {
    setSelectedPromiseForModal(promise);
    setCallModalOpen(true);
  };

  const handleCreateTask = (promise: PromiseToPay) => {
    setSelectedPromiseForModal(promise);
    setTaskModalOpen(true);
  };

  const handleCaseNumberClick = (caseNumber: string) => {
    // Navigate to full-screen case details page
    navigate(`/collections/queue/case/${caseNumber}`, {
      state: { from: "promises" }
    });
  };

  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { variant: 'default' | 'destructive' | 'secondary' | 'outline'; className: string }> = {
      Active: { variant: 'default', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { variant: 'default', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': { variant: 'secondary', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
      Closed: { variant: 'outline', className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };

  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <TrendingUp className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  const handleCreateNewPromise = () => {
    navigate('/collections/promises/create');
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(promises.map(x => x.id));
      setSelectedPromiseIds(allIds);
    } else {
      setSelectedPromiseIds(new Set());
    }
  };

  const handleSelectPromise = (promiseId: string, checked: boolean) => {
    const newSelection = new Set(selectedPromiseIds);
    if (checked) {
      newSelection.add(promiseId);
    } else {
      newSelection.delete(promiseId);
    }
    setSelectedPromiseIds(newSelection);
  };

  const isAllSelected = promises.length > 0 && selectedPromiseIds.size === promises.length;
  const isSomeSelected = selectedPromiseIds.size > 0 && selectedPromiseIds.size < promises.length;

  // Bulk action handlers
  const handleBulkViewDetails = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(x => x.id === promiseId);
      if (promise) {
        handleViewDetails(promise);
      }
    } else {
      toast.info(`Viewing details for ${selectedPromiseIds.size} selected promises`);
    }
  };

  const handleBulkEmail = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(x => x.id === promiseId);
      if (promise) {
        handleEmailCustomer(promise);
      }
    } else {
      toast.success(`Email sent to ${selectedPromiseIds.size} customers`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleBulkCall = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(x => x.id === promiseId);
      if (promise) {
        handleLogCall(promise);
      }
    } else {
      toast.info(`Logging calls for ${selectedPromiseIds.size} customers`);
    }
  };

  const handleBulkTask = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(x => x.id === promiseId);
      if (promise) {
        handleCreateTask(promise);
      }
    } else {
      toast.success(`Created follow-up tasks for ${selectedPromiseIds.size} promises`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleClearSelection = () => {
    setSelectedPromiseIds(new Set());
  };

  const handleBulkReassignUser = () => {
    setReassignUserModalOpen(true);
  };

  const handleBulkUpdateStatus = () => {
    setUpdateStatusModalOpen(true);
  };

  const bulkReassignMutation = useBulkReassignPromises();
  const bulkUpdateStatusMutation = useBulkUpdatePromiseStatus();

  const reassignUserOptions = useMemo(() => {
    if (!usersList) return [];
    return usersList.map(x => ({
      id: String(x.id),
      name: `${x.firstName} ${x.lastName}`.trim(),
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(`${x.firstName} ${x.lastName}`.trim())}&background=random&size=128`,
    }));
  }, [usersList]);

  const handleReassignUserSave = (userId: string, notes: string) => {
    const promiseIds = Array.from(selectedPromiseIds).map(Number);
    bulkReassignMutation.mutate(
      { promiseIds, assignedUserId: Number(userId), notes: notes || undefined },
      {
        onSuccess: () => {
          setSelectedPromiseIds(new Set());
        },
        onError: (error) => {
          toast.error(`Failed to reassign: ${error.message}`);
        },
      }
    );
  };

  const handleUpdateStatusSave = (status: string, notes: string) => {
    const promiseIds = Array.from(selectedPromiseIds).map(Number);
    bulkUpdateStatusMutation.mutate(
      { promiseIds, status, notes: notes || undefined },
      {
        onSuccess: () => {
          setSelectedPromiseIds(new Set());
        },
        onError: (error) => {
          toast.error(`Failed to update status: ${error.message}`);
        },
      }
    );
  };

  // Metric card click handlers
  const handleMetricCardClick = (filter: 'all' | 'active' | 'broken' | 'dueThisWeek') => {
    if (metricFilter === filter) {
      setMetricFilter('all');
      setStatusFilter('all');
    } else {
      setMetricFilter(filter);
      if (filter === 'active') {
        setStatusFilter('Active');
      } else if (filter === 'broken') {
        setStatusFilter('Broken');
      } else if (filter === 'dueThisWeek') {
        // Due This Week is date-range only; preserve current status filter
      } else {
        setStatusFilter('all');
      }
    }
    setCurrentPage(1);
  };

  // Export handler - current view
  const handleExportToExcel = () => {
    try {
      exportPromisesToExcel(promises);
      toast.success(`Exported ${promises.length} promise(s) to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };

  // Export handler - all columns
  const handleExportAllColumns = () => {
    try {
      exportPromisesToExcel(promises, `promises-all-columns-${new Date().toISOString().split('T')[0]}.csv`);
      toast.success(`Exported ${promises.length} promise(s) with all columns to Excel`);
    } catch (error) {
      toast.error('Failed to export promises');
      console.error('Export error:', error);
    }
  };

  const renderColumnHeader = (colId: string): React.ReactNode => {
    switch (colId) {
      case 'select':
        return (
          <TableHead key={colId} className="w-12">
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={handleSelectAll}
              aria-label="Select all promises"
              className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
            />
          </TableHead>
        );
      case 'promiseId':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("promiseId")}>
              <span className="font-bold">Promise ID</span>
              {getSortIcon("promiseId")}
            </Button>
          </TableHead>
        );
      case 'customerName':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("customerName")}>
              <span className="font-bold">Customer</span>
              {getSortIcon("customerName")}
            </Button>
          </TableHead>
        );
      case 'customerAccountNumber':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("customerAccountNumber")}>
              <span className="font-bold">Account Number</span>
              {getSortIcon("customerAccountNumber")}
            </Button>
          </TableHead>
        );
      case 'caseNumber':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("caseNumber")}>
              <span className="font-bold">Case Number</span>
              {getSortIcon("caseNumber")}
            </Button>
          </TableHead>
        );
      case 'lastAction':
        return <TableHead key={colId}><span className="font-bold">Last Action</span></TableHead>;
      case 'promiseDate':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("promiseDate")}>
              <span className="font-bold">Promise Date</span>
              {getSortIcon("promiseDate")}
            </Button>
          </TableHead>
        );
      case 'daysOverdue':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("daysOverdue")}>
              <span className="font-bold">Days Overdue</span>
              {getSortIcon("daysOverdue")}
            </Button>
          </TableHead>
        );
      case 'amount':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("amount")}>
              <span className="font-bold">Amount</span>
              {getSortIcon("amount")}
            </Button>
          </TableHead>
        );
      case 'transactions':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("transactions")}>
              <span className="font-bold">Transactions</span>
              {getSortIcon("transactions")}
            </Button>
          </TableHead>
        );
      case 'status':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("status")}>
              <span className="font-bold">Status</span>
              {getSortIcon("status")}
            </Button>
          </TableHead>
        );
      case 'method':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("method")}>
              <span className="font-bold">Method</span>
              {getSortIcon("method")}
            </Button>
          </TableHead>
        );
      case 'customerEmail':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("customerEmail")}>
              <span className="font-bold">Email</span>
              {getSortIcon("customerEmail")}
            </Button>
          </TableHead>
        );
      case 'customerPhone':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("customerPhone")}>
              <span className="font-bold">Phone</span>
              {getSortIcon("customerPhone")}
            </Button>
          </TableHead>
        );
      case 'createdDate':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("createdDate")}>
              <span className="font-bold">Created Date</span>
              {getSortIcon("createdDate")}
            </Button>
          </TableHead>
        );
      case 'createdBy':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("createdBy")}>
              <span className="font-bold">Created By</span>
              {getSortIcon("createdBy")}
            </Button>
          </TableHead>
        );
      case 'notes':
        return (
          <TableHead key={colId}>
            <Button variant="ghost" size="sm" className="-ml-3 h-8 data-[state=open]:bg-accent" onClick={() => handleSort("notes")}>
              <span className="font-bold">Notes</span>
              {getSortIcon("notes")}
            </Button>
          </TableHead>
        );
      default:
        return null;
    }
  };

  const renderColumnCell = (colId: string, promise: PromiseToPay): React.ReactNode => {
    switch (colId) {
      case 'select':
        return (
          <TableCell key={colId}>
            <Checkbox
              checked={selectedPromiseIds.has(promise.id)}
              onCheckedChange={(checked) => handleSelectPromise(promise.id, checked as boolean)}
              aria-label={`Select promise ${promise.id}`}
            />
          </TableCell>
        );
      case 'promiseId':
        return (
          <TableCell key={colId}>
            <button
              onClick={() => handleViewDetails(promise)}
              className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
            >
              {promise.id}
            </button>
          </TableCell>
        );
      case 'customerName':
        return (
          <TableCell key={colId}>
            <button
              onClick={() => handleViewDetails(promise)}
              className="text-left hover:underline text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
            >
              {promise.customerName}
            </button>
          </TableCell>
        );
      case 'customerAccountNumber':
        return <TableCell key={colId}>{promise.customerAccountNumber}</TableCell>;
      case 'caseNumber':
        return (
          <TableCell key={colId}>
            {promise.caseNumber ? (
              <button
                onClick={() => handleCaseNumberClick(promise.caseNumber)}
                className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
              >
                {promise.caseNumber}
              </button>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        );
      case 'lastAction':
        return (
          <TableCell key={colId}>
            {promise.lastAction ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded">
                      <Info className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-semibold text-sm capitalize">{promise.lastAction.type.replace('_', ' ')}</p>
                      <p className="text-xs">{promise.lastAction.description}</p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                        <span>{promise.lastAction.performedBy}</span>
                        <span>{format(promise.lastAction.date, 'dd/MM/yyyy')}</span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        );
      case 'promiseDate':
        return <TableCell key={colId}>{format(promise.promiseDate, 'dd/MM/yyyy')}</TableCell>;
      case 'daysOverdue':
        return (
          <TableCell key={colId}>
            {promise.daysOverdue && promise.daysOverdue > 0 ? (
              <span className="text-red-600 font-medium">{promise.daysOverdue}</span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </TableCell>
        );
      case 'amount':
        return <TableCell key={colId}><div className="font-medium">{formatCurrency(promise.promisedAmount)}</div></TableCell>;
      case 'transactions':
        return <TableCell key={colId}><div className="text-sm">{promise.invoiceCount ?? 0}</div></TableCell>;
      case 'status':
        return <TableCell key={colId}>{getStatusBadge(promise.status)}</TableCell>;
      case 'method':
        return <TableCell key={colId}>{getConfirmationMethodBadge(promise.confirmationMethod)}</TableCell>;
      case 'customerEmail':
        return <TableCell key={colId}><span className="text-sm">{promise.customerEmail || '-'}</span></TableCell>;
      case 'customerPhone':
        return <TableCell key={colId}><span className="text-sm">{promise.customerPhone || '-'}</span></TableCell>;
      case 'createdDate':
        return <TableCell key={colId}><span className="text-sm">{promise.createdAt ? format(new Date(promise.createdAt), 'dd/MM/yyyy') : '-'}</span></TableCell>;
      case 'createdBy':
        return <TableCell key={colId}><span className="text-sm">{promise.createdBy || '-'}</span></TableCell>;
      case 'notes':
        return <TableCell key={colId}><span className="text-sm truncate max-w-xs">{promise.notes || '-'}</span></TableCell>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="h-8 w-8" />
            Promise to Pay Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track customer payment commitments and monitor promise fulfillment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateNewPromise} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create New Promise
          </Button>
          <HelpSupportButton screenId="promises" />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card 
          className={`border-l-4 border-l-green-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'active' ? 'ring-2 ring-green-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('active')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Promises</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.activePromises.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.activePromises.totalValue)} total value
            </p>
            {metricFilter === 'active' && (
              <p className="text-xs text-green-600 font-medium mt-1">
                ✓ Filtering active promises
              </p>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-red-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'broken' ? 'ring-2 ring-red-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('broken')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Broken Promises</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.brokenPromises.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.brokenPromises.totalValue)} at risk
            </p>
            {metricFilter === 'broken' && (
              <p className="text-xs text-red-600 font-medium mt-1">
                ✓ Filtering broken promises
              </p>
            )}
          </CardContent>
        </Card>

        <Card 
          className={`border-l-4 border-l-amber-500 cursor-pointer transition-all hover:shadow-md ${
            metricFilter === 'dueThisWeek' ? 'ring-2 ring-amber-500 shadow-md' : ''
          }`}
          onClick={() => handleMetricCardClick('dueThisWeek')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.dueThisWeek.count}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(kpis.dueThisWeek.totalValue)} expected
            </p>
            {metricFilter === 'dueThisWeek' && (
              <p className="text-xs text-amber-600 font-medium mt-1">
                ✓ Filtering promises due this week
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Promised Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-shrink-0">
              <CardTitle>Promises ({pagination.totalItems})</CardTitle>
              <CardDescription>
                All payment promises sorted by promise date (oldest first)
              </CardDescription>
            </div>
            
            {/* Search Bar and Status Filter */}
            <div className="flex items-center gap-2 flex-1 max-w-3xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by customer name, account number..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9"
                />
              </div>
              <Select 
                value={statusFilter} 
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setMetricFilter('all');
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Broken">Broken</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={assignedUserFilter} 
                onValueChange={(value) => {
                  setAssignedUserFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {userFilterOptions.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.avatarUrl} alt={user.name} />
                          <AvatarFallback className="text-xs">
                            {user.name.split(' ').map(x => x[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <span>{user.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Configure Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((col) => renderColumnHeader(col.id))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="h-64 text-center">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm text-muted-foreground">Loading promises...</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && promises.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="text-center text-muted-foreground py-8">
                        No promises found matching your criteria
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && promises.map((promise) => {
                    const isSelected = selectedPromiseIds.has(promise.id);
                    return (
                      <TableRow 
                        key={promise.id}
                        className={isSelected ? "bg-muted/50" : ""}
                      >
                        {visibleColumns.map((col) => renderColumnCell(col.id, promise))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

          {/* Pagination Controls */}
          {pagination.totalItems > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, pagination.totalItems)} of {pagination.totalItems} promises
                </span>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        className={cn(
                          currentPage === 1 && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                    
                    {pageNumbers.map((page, index) => (
                      <PaginationItem key={`${page}-${index}`}>
                        {typeof page === 'number' ? (
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        ) : (
                          <PaginationEllipsis />
                        )}
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        className={cn(
                          currentPage === totalPages && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Modal */}
      <SendEmailModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        customerName={selectedPromiseForModal?.customerName || ''}
        customerEmail={selectedPromiseForModal?.customerEmail || ''}
        customerId={selectedPromiseForModal?.customerId}
        sourceType="promise"
        sourceId={selectedPromiseForModal?.id}
      />

      {/* Call Log Modal */}
      <LogCallModal
        open={callModalOpen}
        onOpenChange={setCallModalOpen}
        customerName={selectedPromiseForModal?.customerName || ''}
        customerPhone={selectedPromiseForModal?.customerPhone}
        promiseId={selectedPromiseForModal?.id}
      />

      {/* Reassign User Modal */}
      <ReassignUserModal
        open={reassignUserModalOpen}
        onOpenChange={setReassignUserModalOpen}
        selectedCount={selectedPromiseIds.size}
        onSave={handleReassignUserSave}
        users={reassignUserOptions}
        isLoadingUsers={isLoadingUsers}
      />

      {/* Update Status Modal */}
      <UpdateStatusModal
        open={updateStatusModalOpen}
        onOpenChange={setUpdateStatusModalOpen}
        selectedCount={selectedPromiseIds.size}
        onSave={handleUpdateStatusSave}
      />

      {/* Task Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-up Task</DialogTitle>
            <DialogDescription>
              Schedule a follow-up action for {selectedPromiseForModal?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Task Type</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Follow-up Call</SelectItem>
                  <SelectItem value="email">Send Email</SelectItem>
                  <SelectItem value="review">Review Promise</SelectItem>
                  <SelectItem value="escalate">Escalate Case</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Task details..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Task created successfully');
              setTaskModalOpen(false);
            }}>
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Floating Actions Bar */}
      {selectedPromiseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedPromiseIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedPromiseIds.size === 1 ? "promise" : "promises"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions - Show different buttons based on selection count */}
              <div className="flex items-center gap-2">
                {selectedPromiseIds.size === 1 ? (
                  // Single selection - show all 4 buttons
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkEmail}
                      className="gap-2"
                    >
                      <Mail className="h-4 w-4" />
                      Send Email
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkCall}
                      className="gap-2"
                    >
                      <Phone className="h-4 w-4" />
                      Log Call
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkReassignUser}
                      className="gap-2"
                    >
                      <UserCog className="h-4 w-4" />
                      Reassign User
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkUpdateStatus}
                      className="gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Update Status
                    </Button>
                  </>
                ) : (
                  // Multiple selection - show Reassign User and Update Status only
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkReassignUser}
                      className="gap-2"
                    >
                      <UserCog className="h-4 w-4" />
                      Reassign User
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkUpdateStatus}
                      className="gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Update Status
                    </Button>
                  </>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromisesPage;
